// Live scan demo: the landing page's QR opens a product page with
// `?live=<sid>`. The sid is the read key for that visitor's demo scans, so it
// must never reach analytics: config/posthog.ts takes it out of the address
// bar before PostHog records its first URL, and the product request sends it
// once (publicProductService.getPublicProduct).

// Same format the API accepts (96+ random bits, base64url).
const LIVE_SID_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

let pending: string | undefined;

// Strip `live` from the address bar, keeping react-router's history state
// key, and hold a valid sid for the product request. A malformed one is
// stripped but not kept.
export const captureLiveDemoSid = (): void => {
  if (typeof window === 'undefined') return;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  if (!url.searchParams.has('live')) return;

  const sid = url.searchParams.get('live') ?? '';
  url.searchParams.delete('live');
  try {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (e) {
    // Cosmetic only: never let it break product loading or lose the ping.
    console.warn('Failed to strip the live param from the URL:', e);
  }
  pending = LIVE_SID_PATTERN.test(sid) ? sid : undefined;
};

// The sid, once per page load: later product requests on the page
// (StrictMode's second effect run, a client-side navigation to another
// product) no longer carry it, so one scan makes one ping. Captures first in
// case the address bar still carries it.
export const consumeLiveDemoSid = (): string | undefined => {
  captureLiveDemoSid();
  const sid = pending;
  pending = undefined;
  return sid;
};

// `url` without its `live` param, everything else untouched. For URLs that
// analytics records outside the address bar (replay network entries).
export const withoutLiveParam = (url: string): string =>
  url.replace(/([?&])live=[^&#]*&?/g, '$1').replace(/[?&](?=#|$)/, '');
